/**
 * The paperwork of a sale: the offer letter, and the documents attached to a purchase.
 *
 * **A developer's sale runs on paper the buyer expects.** An offer letter when something is
 * reserved, a deed of assignment at handover, and a survey plan. The last two arrive from a
 * solicitor or a surveyor and are uploaded; the first is ours to produce, because every figure on
 * it is already in the ledger and making somebody retype a twenty-four-row schedule into a word
 * processor is how the schedule and the ledger end up disagreeing.
 *
 * **The one rule this file exists to enforce: the letter cannot disagree with the ledger.** It is
 * handed the purchase's own instalment rows and refuses — loudly, with a `OfferLetterError` — if
 * they do not add up to the agreed price, or if the deposit on the letter is not the deposit on
 * row zero. A document that says one thing while the statement says another is worse than no
 * document at all: it is the thing a buyer produces two years later, and the developer has to
 * explain. So the offer letter is *composed* from `planSchedule`/`customSchedule` output rather
 * than computed a second time here, and there is deliberately no arithmetic in this file beyond
 * summing what it was given and checking the sum.
 *
 * **The wording works for every kind of thing a developer sells.** A scheme selling bare land says
 * "plot"; one selling houses says "home"; one selling shop units says "unit" — `soldNoun()` from
 * `development.ts` decides, and `describePlot()` says what the thing is, so a block of flats sold
 * as one item reads as "Block of flats · 12 flats" rather than as a plot of land.
 *
 * **No country, no continent, no currency is named.** Amounts are formatted with the
 * organisation's own symbol by `formatMoney`, and the letter never says what that symbol is. This
 * is the hardest place in the product to hold that line and it is held anyway.
 *
 * Money is an integer count of minor units and dates that mean a day are `YYYY-MM-DD` strings —
 * ADR-0003. There is no float in this file: percentages come from basis points and are rendered
 * by integer division.
 */

import { formatDate, type PlainDate } from "./dates";
import { formatMoney } from "./money";
import { describePlot, soldNoun } from "./development";
import { scheduleTotal, type PurchaseFrequency, type ScheduledInstalment } from "./purchase";

/* ------------------------------------------------------------ what is attached */

/**
 * The kinds of document a sale carries.
 *
 * A real list rather than free text for the same reason `plots.kind` is one: it is what somebody
 * filters and sorts by, and "Deed" typed six different ways is a column nothing can be built on.
 * `other` is present so nobody is forced to mislabel a thing to get it uploaded.
 *
 * **`offer_letter` and `signed_offer_letter` are two kinds on purpose.** The first is a letter, from
 * wherever it came; the second is *this* letter after somebody signed it by hand and sent it back,
 * and it is the only kind here that is a claim about a thing that happened rather than a file that
 * exists. Merging them would put an unsigned draft and a countersigned copy in one column, and the
 * column is what the sale screen reads. See `SIGNED_COPY_STATEMENT` below and ADR-0162.
 */
export const SALE_DOCUMENT_KINDS = [
  "offer_letter",
  "signed_offer_letter",
  "deed_of_assignment",
  "survey_plan",
  "allocation_letter",
  "other",
] as const;

export type SaleDocumentKind = (typeof SALE_DOCUMENT_KINDS)[number];

export interface SaleDocumentInfo {
  value: SaleDocumentKind;
  label: string;
  /** What it is and when it is issued — shown beside the picker, because these are not synonyms. */
  hint: string;
}

export const SALE_DOCUMENT_INFO: Record<SaleDocumentKind, SaleDocumentInfo> = {
  offer_letter: {
    value: "offer_letter",
    label: "Offer letter",
    hint: "Issued on reservation. It states the price, the plan and the schedule that were agreed.",
  },
  signed_offer_letter: {
    value: "signed_offer_letter",
    label: "Offer letter — signed copy",
    hint:
      "The letter after somebody signed it by hand. It is a copy that was uploaded and is asserted to " +
      "be signed; the signing itself was not witnessed here.",
  },
  deed_of_assignment: {
    value: "deed_of_assignment",
    label: "Deed of assignment",
    hint: "Executed at handover, once the purchase price has been received in full.",
  },
  survey_plan: {
    value: "survey_plan",
    label: "Survey plan",
    hint: "The surveyed extent of what was sold, prepared by a registered surveyor.",
  },
  allocation_letter: {
    value: "allocation_letter",
    label: "Letter of allocation",
    hint: "Confirms which particular one of them the buyer has been allocated.",
  },
  other: {
    value: "other",
    label: "Other document",
    hint: "Anything else that belongs on this file — correspondence, an identity document, a receipt.",
  },
};

export function isSaleDocumentKind(value: unknown): value is SaleDocumentKind {
  return typeof value === "string" && (SALE_DOCUMENT_KINDS as readonly string[]).includes(value);
}

/** "Deed of assignment" for a known kind; the raw value is never shown to anybody. */
export function saleDocumentLabel(kind: string | null | undefined): string {
  return isSaleDocumentKind(kind) ? SALE_DOCUMENT_INFO[kind].label : SALE_DOCUMENT_INFO.other.label;
}

/** The one kind that is a copy of a document this product produced, come back with ink on it. */
export const SIGNED_OFFER_LETTER_KIND: SaleDocumentKind = "signed_offer_letter";

/**
 * What an uploaded signed copy is, in the words every surface has to use.
 *
 * **This is a constant rather than a sentence each screen writes**, and the reason is the same one
 * `PAPER_SIGNATURE_STATEMENT` exists for on the letting side: an uploaded scan is *a copy somebody
 * asserted is signed*, and nothing about it was witnessed here. Six facts are known about a name
 * typed into the portal — who was signed in, when, from where, with what browser, against which
 * fingerprint — and **not one of them is available about a photograph of a page**. A screen that
 * wrote its own wording would eventually write "signed", and the product would be claiming
 * something it cannot support about a document a buyer keeps.
 *
 * So: what is recorded is the file, the member of staff who attached it, and the moment it arrived.
 * Everything else is on the face of the paper and is the reader's to judge.
 *
 * **Deliberately not a day the paper was signed.** The agreement records one because an agreement
 * has signature blocks with state; an offer letter has none, and a second unwitnessed claim —
 * an asserted date, sitting next to `acceptBy` — would invite somebody to conclude from two
 * assertions that the offer was accepted in time. The arrival time is a fact; the ink's date is
 * not, and the letter is not the place to guess. See ADR-0162.
 */
export const SIGNED_COPY_STATEMENT =
  "An uploaded copy, asserted to be signed. The signing was not witnessed here — what is recorded is " +
  "the file, who attached it and when it arrived.";

/* ---------------------------------------------------------------- the letter */

export class OfferLetterError extends Error {}

export interface LetterParty {
  name: string;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
}

export interface OfferLetterInput {
  /** The purchase's own reference. Every payment has to quote it, so it leads the letter. */
  reference: string;
  /** The day the letter is produced. Passed in, never read from a clock — this file is pure. */
  issuedOn: PlainDate;
  agency: LetterParty;
  buyer: LetterParty;
  estate: {
    name: string;
    addressLine?: string | null;
    city?: string | null;
    state?: string | null;
    /** The title the scheme is sold on. The first thing a buyer asks about. */
    titleType?: string | null;
    /** What the scheme sells, which decides every noun in the letter. */
    sells?: string | null;
  };
  /**
   * The particular thing allocated, or null.
   *
   * **Null is ordinary, not an error.** Somebody buys into a scheme first and is allocated a
   * specific one later, sometimes by ballot — so the letter says so in as many words rather than
   * leaving a blank where the address should be.
   */
  property: {
    label: string;
    kind?: string | null;
    units?: number | null;
    sizeSqm?: number | null;
  } | null;
  /** What was agreed, in minor units. */
  price: number;
  /** Payable before the schedule starts. May be zero. */
  deposit: number;
  frequency: PurchaseFrequency;
  /** The purchase's own instalment rows, sequence 0 first. The ledger, not a recomputation. */
  schedule: ScheduledInstalment[];
  /** The organisation's currency code. Only ever handed to `formatMoney`; never named in words. */
  currency: string;
  /** A late payment charge, in basis points of what is still owed. Zero means none is mentioned. */
  lateFeeBps: number;
  /** A discount for settling ahead of schedule, in basis points. Zero means none is mentioned. */
  earlyDiscountBps: number;
  /**
   * The reservation this sale came out of, if it came out of one.
   *
   * **Null on most letters and that is ordinary** — a developer who takes no reservations, and
   * every walk-in sale, has nothing here.
   *
   * When it is not null the letter has to say so, because the buyer has *already paid* part of the
   * price and the schedule below does not show it. The fee counts toward the price rather than
   * sitting on top of it, so row zero of the schedule is the deposit in full and the money already
   * received is allocated against it. A letter that omits that reads, to the person who paid it,
   * as a demand for the same money twice.
   *
   * `paid` is what has actually been received against the reservation, which is not always the fee
   * that was quoted: somebody pays half of it, and the letter must say what arrived rather than
   * what was agreed. Zero — quoted but never paid — says nothing about money and only names the
   * reservation, which is still worth saying because the buyer has its reference.
   */
  reservation?: {
    reference: string;
    reservedOn: PlainDate;
    /** In minor units. What was actually received, not what was quoted. */
    paid: number;
    /** Whether the fee comes back if this comes to nothing. It is a condition, so it is printed. */
    refundable: boolean;
  } | null;
}

export interface OfferLetterLine {
  sequence: number;
  label: string;
  dueDate: PlainDate;
  /** The day, written out, so a letter never carries an ambiguous numeric date. */
  dueDateLabel: string;
  amount: number;
  amountLabel: string;
}

export interface OfferLetter {
  reference: string;
  issuedOn: PlainDate;
  issuedOnLabel: string;
  /**
   * The day the offer stops standing: the day the first money on the schedule falls due.
   *
   * Derived from the ledger rather than being a number somebody chose, so the letter cannot
   * promise a window the schedule contradicts.
   */
  acceptBy: PlainDate;
  acceptByLabel: string;
  subject: string;
  salutation: string;
  /** The body, one string per paragraph. Formal, and free of any placeholder left unfilled. */
  paragraphs: string[];
  schedule: OfferLetterLine[];
  totals: {
    price: number;
    priceLabel: string;
    deposit: number;
    depositLabel: string;
    /** Price less deposit — what the instalments have to add up to. */
    balance: number;
    balanceLabel: string;
    /** How many payments there are after the deposit. */
    instalments: number;
    /** What the schedule adds up to. Equal to the price, or this letter was never produced. */
    total: number;
    totalLabel: string;
  };
  /** The conditions, as a list. A paragraph nobody reads is a condition nobody agreed to. */
  terms: string[];
  signatures: { developer: string; buyer: string };
  /**
   * The reservation this came out of, echoed back with its figure already formatted.
   *
   * Null on most letters. It is repeated here rather than left inside the prose so a screen can put
   * it in the summary block beside the price without parsing a paragraph — and so the two can never
   * disagree, because both are built from the same input in this one function.
   */
  reservation: { reference: string; reservedOn: PlainDate; paid: number; paidLabel: string; refundable: boolean } | null;
  /** What one and many of these are called, so a screen rendering the letter agrees with it. */
  noun: { one: string; many: string };
  /** What is being sold, in words. "Block of flats · 12 flats", or the estate itself. */
  describes: string;
}

/** How one period of this frequency reads in a sentence. `custom` never reaches here. */
const CADENCE: Record<Exclude<PurchaseFrequency, "custom">, string> = {
  monthly: "monthly",
  quarterly: "quarterly",
  biannual: "half-yearly",
  annual: "annual",
};

/**
 * Basis points as a percentage, without a float.
 *
 * 250 becomes "2.5", 25 becomes "0.25", 500 becomes "5". Doing this with division would put
 * `0.30000000000000004` on a document somebody signs, which is exactly the class of thing ADR-0003
 * exists to prevent — so it is integer arithmetic and string assembly.
 */
export function percentFromBps(bps: number): string {
  const whole = Math.floor(bps / 100);
  const rest = bps % 100;
  if (rest === 0) return String(whole);
  const decimals = String(rest).padStart(2, "0").replace(/0$/, "");
  return `${whole}.${decimals}`;
}

/**
 * Produce the letter.
 *
 * Throws rather than returning something approximate. The three refusals are all the same
 * refusal: **the letter may not say anything the ledger does not.**
 */
export function offerLetter(input: OfferLetterInput): OfferLetter {
  if (!input.schedule.length) {
    throw new OfferLetterError("This purchase has no payment schedule, so there is nothing to offer.");
  }

  const ordered = [...input.schedule].sort((a, b) => a.sequence - b.sequence);
  const first = ordered[0];

  if (first.sequence !== 0) {
    throw new OfferLetterError("This purchase's schedule does not begin at its first payment.");
  }

  // The deposit on the letter is row zero, or the two disagree the moment somebody reads down the
  // column — and the column is on the same page.
  if (first.amount !== input.deposit) {
    throw new OfferLetterError(
      `The deposit recorded on this purchase is ${formatMoney(input.deposit, input.currency)} and the first ` +
        `payment on its schedule is ${formatMoney(first.amount, input.currency)}. They have to be the same figure.`,
    );
  }

  const total = scheduleTotal(ordered);
  if (total !== input.price) {
    throw new OfferLetterError(
      `The schedule adds up to ${formatMoney(total, input.currency)} and the agreed price is ` +
        `${formatMoney(input.price, input.currency)}. A letter that disagrees with the statement is worse than ` +
        `no letter, so this one is not produced until they match.`,
    );
  }

  const money = (amount: number) => formatMoney(amount, input.currency);
  const noun = soldNoun(input.estate.sells);
  const describes = input.property
    ? describePlot(input.property.kind, input.property.units)
    : noun.one;

  const instalments = ordered.filter((row) => row.sequence > 0);
  const balance = input.price - input.deposit;

  /* The offer stands until the first money is due. Any other window would be a number invented
     here that the schedule on the same page contradicts. */
  const acceptBy = first.dueDate;

  const where = [input.estate.addressLine, input.estate.city, input.estate.state].filter(Boolean).join(", ");
  const at = where ? `${input.estate.name}, ${where}` : input.estate.name;

  const subject = input.property
    ? `Offer of ${describes} — ${input.property.label}, ${input.estate.name}`
    : `Offer of ${noun.one} — ${input.estate.name}`;

  const paragraphs: string[] = [];

  paragraphs.push(
    input.property
      ? `Further to your application, we are pleased to offer you ${input.property.label} at ${at}` +
        `${input.property.sizeSqm ? `, measuring approximately ${input.property.sizeSqm} square metres` : ""}` +
        `, on the terms set out below.`
      : `Further to your application, we are pleased to offer you one ${noun.one} at ${at}, on the terms set ` +
        `out below. A particular ${noun.one} has not been allocated to you at the date of this letter.`,
  );

  paragraphs.push(
    input.deposit > 0
      ? `The agreed purchase price is ${money(input.price)}. Of that, ${money(input.deposit)} is payable on ` +
        `acceptance of this offer, and the balance of ${money(balance)} is payable as set out in the schedule below.`
      : `The agreed purchase price is ${money(input.price)}, payable in full as set out in the schedule below.`,
  );

  if (instalments.length) {
    const last = instalments[instalments.length - 1];
    paragraphs.push(
      input.frequency === "custom"
        ? `The balance is payable in ${instalments.length} ${instalments.length === 1 ? "payment" : "payments"} on the ` +
          `dates shown, the last falling due on ${formatDate(last.dueDate, { long: true })}.`
        : `The balance is payable in ${instalments.length} ${CADENCE[input.frequency]} ` +
          `${instalments.length === 1 ? "instalment" : "instalments"}, the first falling due on ` +
          `${formatDate(instalments[0].dueDate, { long: true })} and the last on ` +
          `${formatDate(last.dueDate, { long: true })}.`,
    );
  }

  /*
    The reservation, before the conditions and after the money.

    It is placed here rather than in `terms` because it is a statement of fact about payments
    already received, not a condition being imposed — and it sits immediately under the paragraph
    that names the deposit, which is the figure it is credited against. A buyer reading down the
    page meets the price, then the deposit, then what they have already paid toward it.
  */
  if (input.reservation) {
    const held = input.reservation;
    paragraphs.push(
      held.paid > 0
        ? `We acknowledge your reservation ${held.reference} of ${formatDate(held.reservedOn, { long: true })}, ` +
          `against which ${money(held.paid)} has been received. That sum is credited to the purchase price shown ` +
          `above and is applied to the first payment on the schedule below; it is not payable again. ` +
          (held.refundable
            ? `It is refundable should this sale not proceed.`
            : `It is not refundable should this sale not proceed.`)
        : `This offer follows your reservation ${held.reference} of ` +
          `${formatDate(held.reservedOn, { long: true })}.`,
    );
  }

  if (input.estate.titleType) {
    paragraphs.push(`The ${noun.one} is sold on ${input.estate.titleType}.`);
  }

  if (input.lateFeeBps > 0) {
    paragraphs.push(
      `A late payment charge of ${percentFromBps(input.lateFeeBps)} per cent of the amount then outstanding is ` +
        `applied to any instalment which remains unpaid after its due date.`,
    );
  }

  if (input.earlyDiscountBps > 0) {
    paragraphs.push(
      `Should you elect to settle the balance ahead of schedule, a discount of ` +
        `${percentFromBps(input.earlyDiscountBps)} per cent is allowed on the payments not yet fallen due at the ` +
        `date of settlement.`,
    );
  }

  paragraphs.push(
    `This offer is open for acceptance until ${formatDate(acceptBy, { long: true })}. Kindly confirm your ` +
      `acceptance by signing and returning a copy of this letter.`,
  );

  const terms: string[] = [
    `Every payment must quote the reference ${input.reference} so that it is credited to this account.`,
    `Payment is applied to the oldest unpaid instalment first. Any amount received in excess of what is due is ` +
      `held to your credit against the next instalment.`,
  ];

  if (!input.property) {
    terms.push(
      `A particular ${noun.one} will be allocated to you and confirmed in writing. Until then this offer is of ` +
        `one ${noun.one} in the scheme and not of any identified ${noun.one}.`,
    );
  }

  terms.push(
    `Title documents will be executed and released on receipt of the purchase price in full.`,
    `This offer is made subject to the ${noun.one} remaining available at the date your acceptance is received.`,
    /* "Set out in this letter" rather than "below": the conditions are printed under the schedule
       on the page, so "below" pointed at nothing. */
    `The schedule set out in this letter is the schedule of record. Any statement of account we issue is drawn ` +
      `from the same figures.`,
  );

  return {
    reference: input.reference,
    issuedOn: input.issuedOn,
    issuedOnLabel: formatDate(input.issuedOn, { long: true }),
    acceptBy,
    acceptByLabel: formatDate(acceptBy, { long: true }),
    subject,
    salutation: `Dear ${input.buyer.name},`,
    paragraphs,
    schedule: ordered.map((row) => ({
      sequence: row.sequence,
      label: row.label,
      dueDate: row.dueDate,
      dueDateLabel: formatDate(row.dueDate, { long: true }),
      amount: row.amount,
      amountLabel: money(row.amount),
    })),
    totals: {
      price: input.price,
      priceLabel: money(input.price),
      deposit: input.deposit,
      depositLabel: money(input.deposit),
      balance,
      balanceLabel: money(balance),
      instalments: instalments.length,
      total,
      totalLabel: money(total),
    },
    terms,
    signatures: {
      developer: `For and on behalf of ${input.agency.name}`,
      buyer: `Accepted by ${input.buyer.name}`,
    },
    reservation: input.reservation
      ? { ...input.reservation, paidLabel: money(input.reservation.paid) }
      : null,
    noun,
    describes,
  };
}
